import loadable from "@loadable/component";
import React from "react";
import { isMobile, isTablet } from "react-device-detect";
import { Navigate, useLocation } from "react-router-dom";

// Hooks

// Helpers
import {
  hasBrowserSupport,
  hasLandScape,
  hasUserMedia,
  hasWebWorker,
} from "../helpers/appHelper";

// Components
import MainView from "../components/MainView";

// Assets
import exitImg from "../assets/core/img/exit.png";
import helpImg from "../assets/core/img/help.png";
import playImg from "../assets/core/img/play.png";
import rocketImg from "../assets/core/img/rocket.png";
import { toast } from "react-toastify";

// Gameplay view
export default function GamePlay() {
  const location = useLocation();

  const { game, level, option } = location.state || {};

  const isMobileOrTablet = isMobile || isTablet;
  const notSupported =
    !hasBrowserSupport() || !hasUserMedia() || !hasWebWorker();

  const [view, setView] = React.useState("start");
  const [score, setScore] = React.useState(0);
  const [gameOver, setGameOver] = React.useState(false);
  const [start, setStart] = React.useState(false);

  // Check if user is supported
  React.useEffect(() => {
    if (notSupported) {
      setView("error");
    } else if (isMobileOrTablet) {
      toast.info(
        "For best experience, please use a desktop or laptop to play this game"
      );
    }

    if (!hasLandScape()) {
      setView("error");
    }
  }, [isMobileOrTablet, notSupported]);

  if (!game) {
    return <Navigate to="/games" replace />;
  }

  return (
    <MainView design={false} screen logo={false}>
      <div className="bg-black h-full w-full">
        {view === "play" && (
          <div className="bg-black w-full h-12 lg:h-14">
            <ScoreBoard
              level={require(`../games/${game.game_file}`).LEVELS[level]}
              start={start}
              score={score}
              setGameOver={setGameOver}
            />
          </div>
        )}
        <div
          className={`${
            view === "play"
              ? "h-[calc(100%_-_48px)] lg:h-[calc(100%_-_56px)]"
              : "h-full"
          } w-full`}
        >
          {view === "start" ? (
            <Start setView={setView} option={option} />
          ) : view === "play" ? (
            <Play
              level={level}
              start={start}
              gameOver={gameOver}
              setScore={setScore}
              setView={setView}
              setStart={setStart}
              game_file={game.game_file}
            />
          ) : view === "end" ? (
            <End game={game} score={score} />
          ) : view === "help" ? (
            <Help setView={setView} />
          ) : view === "error" ? (
            <Navigate to={`/g/${game.game_id}`} state={{ game }} replace />
          ) : (
            <Navigate to={`/g/${game.game_id}`} state={{ game }} replace />
          )}
        </div>
      </div>
    </MainView>
  );
}

// Start view
function Start({ setView, option }) {
  // Handle play
  const handlePlay = () => {
    switch (option) {
      case "play":
        setView("play");
        break;
      case "tutorial":
        setView("help");
        break;
      case "test":
        setView("test");
        break;
      default:
        setView("play");
        break;
    }
  };

  // Handle help
  const handleHelp = () => {
    setView("help");
  };

  return (
    <div className="bg-black w-full h-full">
      <div className="flex items-center justify-center h-full">
        <div>
          <img
            src={playImg}
            alt=""
            className="w-12 sm:w-14 xl:w-16 cursor-pointer mr-5 xl:mr-8"
            onClick={() => {
              handlePlay();
            }}
          />
        </div>

        <div>
          <img
            src={helpImg}
            alt=""
            className="w-12 sm:w-14 xl:w-16 cursor-pointer ml-5 xl:ml-8"
            onClick={() => {
              handleHelp();
            }}
          />
        </div>
      </div>
    </div>
  );
}

// End view
function End({ game, score }) {
  // Handle exit
  return (
    <Navigate to={`/g/${game.game_id}/end`} state={{ game, score }} replace />
  );
}

// Help view
function Help({ setView }) {
  // Handle start
  const handleStart = () => {
    setView("play");
  };

  return (
    <div className="bg-black w-full h-full relative">
      <div className="bg-retro-grey absolute left-0 right-0 bottom-0 h-1/2 w-full sm:w-1/3 sm:h-full ml-auto">
        <div className="text-black relative p-3">
          <div className="h-6 w-6 absolute right-3 top-3">
            <img
              src={exitImg}
              alt=""
              onClick={() => {
                handleStart();
              }}
              className="h-full w-full cursor-pointer"
            />
          </div>
          <div>
            <p className="text-xl lg:text-2xl">Instructions</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Score board
function ScoreBoard({ level, start, score, setGameOver }) {
  const timerAnimeRef = React.useRef(null);
  const durationRef = React.useRef(level.duration * 1000);
  const timeLeft = React.useRef(durationRef.current);
  const endTime = React.useRef(null);

  const [time, setTime] = React.useState(timeLeft.current);

  React.useEffect(() => {
    const anime = () => {
      if (start) {
        if (endTime.current === null) {
          endTime.current = new Date().getTime() + durationRef.current;
        }

        timeLeft.current = endTime.current - new Date().getTime();
        setTime(timeLeft.current);

        if (timeLeft.current <= 0) {
          setGameOver(true);
          cancelAnimationFrame(timerAnimeRef.current);
        } else {
          timerAnimeRef.current = requestAnimationFrame(anime);
        }
      }
    };

    timerAnimeRef.current = requestAnimationFrame(anime);

    return () => {
      cancelAnimationFrame(timerAnimeRef.current);
    };
  }, [timeLeft, setGameOver, start, endTime]);

  return (
    <div className="bg-black w-full sm:h-12 lg:h-14 text-white px-[3vw]">
      <div className="h-full flex sm:justify-center relative">
        <div className="h-full w-36 flex items-center justify-center">
          <p className="text-white font-retro-rounded font-bold leading-none text-[220%] lg:text-[280%] text-center">
            {new Date(time).toLocaleTimeString([], {
              minute: "2-digit",
              second: "2-digit",
            })}
          </p>
        </div>
        <div className="absolute right-0 top-0 h-full flex items-center justify-between">
          <div className="w-8 h-8 lg:h-10 lg:w-10 mr-4">
            <img src={rocketImg} alt="" className="w-full h-full" />
          </div>
          <p className="text-white font-retro-rounded font-bold leading-none text-[220%] lg:text-[280%] text-center">
            {score.toLocaleString([], {
              minimumIntegerDigits: 2,
              useGrouping: false,
            })}
          </p>
        </div>
      </div>
    </div>
  );
}

// Play veiew
const Play = loadable((props) => import(`../games/${props.game_file}`), {
  fallback: <div>Loading...</div>,
  cacheKey: (props) => props.game_file,
});
